import { CanonicalContact, ContactRole } from '../helpers/contactShape';
import {
  collectAvailableCategories,
  collectAvailableRoles,
  filterByCategories,
  filterByRoles,
  filterBySearch,
} from '../helpers/contactFilters';
import { groupByRole, GroupedContacts } from '../helpers/groupByRole';
import { useContactPickerData } from './useContactPickerData';
import {
  ContactPickerMultiUIState,
  useContactPickerMultiUIState,
} from './useContactPickerMultiUIState';

export interface SelectedContact {
  userId: string;
  name: string;
  role: string;
  profileImage?: string | null;
}

interface UseContactPickerMultiOrchestratorArgs {
  serviceRequest: any;
  from: any;
  initialSelected: SelectedContact[];
  excludeUserIds: string[];
}

export interface ContactPickerMultiOrchestrator {
  isLoading: boolean;

  selected: SelectedContact[];

  groupedResults: GroupedContacts[];
  availableCategories: string[];
  availableRoles: ContactRole[];

  searchTerm: string;
  selectedRoles: ContactRole[];
  selectedCategories: string[];

  onChangeSearchTerm: (term: string) => void;
  onChangeSelectedRoles: (roles: ContactRole[]) => void;
  onChangeSelectedCategories: (categories: string[]) => void;
  onSelectContact: (contact: CanonicalContact) => void;
  onRemoveSelectedContact: (userId: string) => void;
}

export const useContactPickerMultiOrchestrator = ({
  serviceRequest,
  from,
  initialSelected,
  excludeUserIds,
}: UseContactPickerMultiOrchestratorArgs): ContactPickerMultiOrchestrator => {
  const { allContacts, isLoading } = useContactPickerData({
    serviceRequest,
    from,
  });

  const uiState: ContactPickerMultiUIState =
    useContactPickerMultiUIState(initialSelected);

  const availableCategories: string[] =
    collectAvailableCategories(allContacts);

  const availableRoles: ContactRole[] = collectAvailableRoles(allContacts);

  const afterSearch: CanonicalContact[] = filterBySearch(
    allContacts,
    uiState.searchTerm
  );

  const afterRole: CanonicalContact[] = filterByRoles(
    afterSearch,
    uiState.selectedRoles
  );

  const afterCategory: CanonicalContact[] = filterByCategories(
    afterRole,
    uiState.selectedCategories
  );

  const selectedUserIds: string[] = uiState.selected.map(
    (s: SelectedContact) => s.userId
  );

  const excludedSet: Set<string> = new Set<string>([
    ...excludeUserIds,
    ...selectedUserIds,
  ]);

  const afterExclusion: CanonicalContact[] = afterCategory.filter(
    (contact: CanonicalContact) => !excludedSet.has(contact.id)
  );

  const groupedResults: GroupedContacts[] = groupByRole(afterExclusion);

  return {
    isLoading,
    selected: uiState.selected,
    groupedResults,
    availableCategories,
    availableRoles,
    searchTerm: uiState.searchTerm,
    selectedRoles: uiState.selectedRoles,
    selectedCategories: uiState.selectedCategories,
    onChangeSearchTerm: uiState.onChangeSearchTerm,
    onChangeSelectedRoles: uiState.onChangeSelectedRoles,
    onChangeSelectedCategories: uiState.onChangeSelectedCategories,
    onSelectContact: uiState.onSelectContact,
    onRemoveSelectedContact: uiState.onRemoveSelectedContact,
  };
};
